import React from 'react';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GuidedTourImageSlot from './GuidedTourImageSlot';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  tip?: string;
  targetSelector?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface GuidedTourTooltipProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
  position?: { x: number; y: number };
  tooltipStyle?: React.CSSProperties;
}

const GuidedTourTooltip: React.FC<GuidedTourTooltipProps> = ({
  step,
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onClose,
  position,
  tooltipStyle = {},
}) => {
  const { t } = useTranslation();
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // 解析翻译键，如果是翻译键则进行翻译
  const getTranslatedText = (key: string) => {
    // 检查是否是翻译键格式（包含点号）
    if (key.includes('.')) {
      return t(key);
    }
    return key;
  };

  // 默认位置样式
  const getDefaultPosition = (): React.CSSProperties => {
    if (position) {
      return {
        position: 'fixed' as const,
        left: position.x,
        top: position.y,
        transform: 'translate(20px, 0)',
      };
    }

    // 居中显示
    return {
      position: 'fixed' as const,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    };
  };

  // 获取翻译后的文本
  const translatedTitle = getTranslatedText(step.title);
  const translatedDescription = getTranslatedText(step.description);
  const translatedTip = step.tip ? getTranslatedText(step.tip) : undefined;

  return (
    <div
      ref={tooltipRef}
      style={{
        ...getDefaultPosition(),
        zIndex: 100002,
        width: '480px',
        maxWidth: '90vw',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        ...tooltipStyle,
      }}
    >
      {/* 头部 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* 进度指示器 */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: index === currentStep ? '20px' : '8px',
                  height: '6px',
                  borderRadius: '3px',
                  backgroundColor: index === currentStep ? '#1890ff' : index < currentStep ? '#52c41a' : '#e8e8e8',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
          <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {currentStep + 1} / {totalSteps}
          </span>
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#999',
            fontSize: '16px',
            lineHeight: 1,
          }}
        >
          <CloseOutlined />
        </button>
      </div>

      {/* 内容区域 */}
      <div style={{ padding: '20px' }}>
        {/* 标题 */}
        <h3
          style={{
            margin: 0,
            marginBottom: '8px',
            fontSize: '18px',
            fontWeight: 600,
            color: '#262626',
          }}
        >
          {translatedTitle}
        </h3>

        {/* 描述 */}
        <p
          style={{
            margin: 0,
            marginBottom: '12px',
            fontSize: '14px',
            color: '#595959',
            lineHeight: 1.6,
          }}
        >
          {translatedDescription}
        </p>

        {/* 提示 */}
        {translatedTip && (
          <div
            style={{
              padding: '10px 12px',
              backgroundColor: '#f0f5ff',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#1890ff',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            <span style={{ fontWeight: 600, flexShrink: 0 }}>{t('tour.tipLabel', '提示')}：</span>
            <span>{translatedTip}</span>
          </div>
        )}

        {/* 单个图片槽位 */}
        <div
          style={{
            marginBottom: '16px',
          }}
        >
          <GuidedTourImageSlot
            stepId={step.id}
            position="guide"
          />
        </div>
      </div>

      {/* 底部按钮区域 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderTop: '1px solid #f0f0f0',
          backgroundColor: '#fafafa',
        }}
      >
        {/* 左侧按钮 */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isFirst && (
            <Button
              icon={<LeftOutlined />}
              onClick={onPrevious}
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              {t('tour.buttons.previous', '上一步')}
            </Button>
          )}
        </div>

        {/* 右侧按钮 */}
        <Button
          type="primary"
          onClick={onNext}
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          {isLast ? t('tour.buttons.complete', '完成') : t('tour.buttons.next', '下一步')}
          {!isLast && <RightOutlined />}
        </Button>
      </div>
    </div>
  );
};

export default GuidedTourTooltip;
